import {decodeBase64Url, encodeBase64Url} from './encoding.js';

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const clockTolerance = 30; // seconds

/**
 * Transforms a code verifier into a challenge using the S256 PKCE Code Challenge Method transformation.
 * See https://www.rfc-editor.org/rfc/rfc7636.html#section-4
 * @param {string} codeVerifier The code verifier.
 * @return {Promise<string>} The code challenge.
 */
export async function calculateCodeChallenge(codeVerifier) {
  const encodedVerifier = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', encodedVerifier);
  return encodeBase64Url(new Uint8Array(digest));
}

/**
 * @typedef {Object} AuthenticationRequest
 * @property {Array<string>} scopes List of scopes (must include `openid`).
 * @property {string} responseType The response type (e.g. `code`).
 * @property {string} clientId The client ID.
 * @property {string} redirectUri The redirect URI.
 * @property {string} codeChallenge The PKCE code challenge.
 * @property {string} codeChallengeMethod The PKCE code challenge method.
 * @property {string} [nonce] Opaque value used to associate a client session with an ID token.
 * @property {string} [state] Opaque value used to maintain state between the request and the callback.
 * @property {string} [responseMode] The response mode (e.g. `query`).
 * @property {string} [prompt] Space-delimited list of values that specifies whether the Authorization Server prompts the End-User for reauthentication and consent.
 */

/**
 * Format the authentication request URL.  See https://openid.net/specs/openid-connect-core-1_0.html#AuthRequest for
 * the core OIDC parameters and https://datatracker.ietf.org/doc/html/rfc7636 for the PKCE parameters.
 * @param {string} endpoint The authentication endpoint.
 * @param {AuthenticationRequest} request The authentication request properties.
 * @return {string} The authentication request URL.
 */
export function formatAuthenticationRequest(endpoint, request) {
  const url = new URL(endpoint);
  const params = url.searchParams;
  params.set('scope', request.scopes.join(' '));
  params.set('response_type', request.responseType);
  params.set('client_id', request.clientId);
  params.set('redirect_uri', request.redirectUri);
  params.set('code_challenge', request.codeChallenge);
  params.set('code_challenge_method', request.codeChallengeMethod);
  if (request.nonce) {
    params.set('nonce', request.nonce);
  }
  if (request.state) {
    params.set('state', request.state);
  }
  if (request.responseMode) {
    params.set('response_mode', request.responseMode);
  }
  if (request.prompt) {
    params.set('prompt', request.prompt);
  }
  return url.href;
}

/**
 * @typedef {Object} AuthenticationResponse
 * @property {string|null} [code] The authorization code.
 * @property {string|null} [state] The state value passed in the request.
 * @property {string|null} [error] The error code.
 * @property {string|null} [errorDescription] The error description.
 * @property {string|null} [errorUri] A URI for more information about the error.
 */

/**
 * @param {URLSearchParams} params The search params.
 * @param {string} key The parameter key.
 * @return {string|null} The parameter value.
 */
function extractSearchParam(params, key) {
  const value = params.get(key);
  params.delete(key);
  return value;
}

/**
 * Extract the authentication response properties from the search parameters.  Matching
 * properties will be deleted from the the search params.
 * @param {URLSearchParams} params The search params.
 * @return {AuthenticationResponse} The authentication response properties.
 */
export function extractAuthenticationResponse(params) {
  const responseParams = {
    code: extractSearchParam(params, 'code'),
    state: extractSearchParam(params, 'state'),
    error: extractSearchParam(params, 'error'),
    errorDescription: extractSearchParam(params, 'error_description'),
    errorUri: extractSearchParam(params, 'error_uri'),
  };
  return responseParams;
}

/**
 * @typedef {Object} TokenRequest
 * @property {string} clientId The client ID.
 * @property {string} grantType The grant type (e.g. `authorization_code`).
 * @property {string} code The authorization code.
 * @property {string} codeVerifier The PKCE code verifier.
 * @property {string} redirectUri The redirect URI.
 */

/**
 * Format the token request parameters as a URLSearchParams object.
 * See https://openid.net/specs/openid-connect-core-1_0.html#TokenRequest.
 * @param {TokenRequest} request The token request.
 * @return {URLSearchParams} The token request search params.
 */
export function formatTokenRequest(request) {
  const params = new URLSearchParams();
  params.set('client_id', request.clientId);
  params.set('grant_type', request.grantType);
  params.set('code', request.code);
  params.set('code_verifier', request.codeVerifier);
  params.set('redirect_uri', request.redirectUri);
  return params;
}

/**
 * @typedef {Object} TokenResponse
 * @property {string} accessToken The access token.
 * @property {string} idToken The ID token.
 * @property {number} expiry The expiry time of the access token (in milliseconds since the epoch).
 */

/**
 * Extract the access token and ID token from the token response body.
 * @param {Object<string, unknown>} body The token response body.
 * @return {TokenResponse} The token response.
 */
export function parseTokenResponse(body) {
  const accessToken = body.access_token;
  if (typeof accessToken !== 'string') {
    throw new Error('Expected a string access token');
  }

  const idToken = body.id_token;
  if (typeof idToken !== 'string') {
    throw new Error('Expected a string ID token');
  }

  /**
   * @type {number}
   */
  let expiry;
  if ('expires_in' in body) {
    const expiresIn = body.expires_in;
    if (typeof expiresIn !== 'number') {
      throw new Error('Expected expires_in to be a number');
    }
    expiry = Date.now() + expiresIn * 1000;
  } else {
    // expires_in is optional, see https://datatracker.ietf.org/doc/html/rfc6749#section-4.2.2
    const accessTokenClaims = parseAccessTokenClaims(accessToken);
    if (!(typeof accessTokenClaims.exp === 'number')) {
      throw new Error('Expected an exp claim in the access token');
    }
    expiry = accessTokenClaims.exp * 1000;
  }

  return {
    accessToken,
    idToken,
    expiry,
  };
}

/**
 * @typedef {Object} ProviderConfig
 * @property {string} issuer The issuer URL.
 * @property {string} authorizationEndpoint The authorization endpoint URL.
 * @property {string} tokenEndpoint The token endpoint URL.
 * @property {string} [endSessionEndpoint] The end session endpoint URL.
 * @property {Array<string>} scopesSupported The supported scopes.
 * @property {Array<string>} codeChallengeMethodsSupported The supported code challenge methods.
 */

/**
 * Parse the provider configuration.
 * See https://openid.net/specs/openid-connect-discovery-1_0.html#ProviderConfig.
 * @param {Object<string, unknown>} body The provider configuration body.
 * @return {ProviderConfig} The provider configuration.
 */
export function parseProviderConfig(body) {
  const issuer = body.issuer;
  if (typeof issuer !== 'string') {
    throw new Error('Expected a string issuer');
  }

  const authorizationEndpoint = body.authorization_endpoint;
  if (typeof authorizationEndpoint !== 'string') {
    throw new Error('Expected a string authorization endpoint');
  }

  const tokenEndpoint = body.token_endpoint;
  if (typeof tokenEndpoint !== 'string') {
    throw new Error('Expected a string token endpoint');
  }

  const endSessionEndpoint = body.end_session_endpoint;
  if (
    endSessionEndpoint !== undefined &&
    typeof endSessionEndpoint !== 'string'
  ) {
    throw new Error('Expected a string end session endpoint');
  }

  const scopesSupported = body.scopes_supported;
  if (!Array.isArray(scopesSupported)) {
    throw new Error('Expected an array of scopes');
  }

  const codeChallengeMethodsSupported = body.code_challenge_methods_supported;
  if (!Array.isArray(codeChallengeMethodsSupported)) {
    throw new Error('Expected an array of code challenge methods');
  }

  return {
    issuer,
    authorizationEndpoint,
    tokenEndpoint,
    endSessionEndpoint,
    scopesSupported,
    codeChallengeMethodsSupported,
  };
}

/**
 * The claims for Planet issued tokens are evolving.
 * See https://docs.google.com/spreadsheets/d/1kKsKbVtVzis6IYxRuxfQbMVSPt2JNxyFr9rR90jB_Q4/
 * for a list of some of the claims.  The types below were generated from that list and
 * from actual tokens.
 */

/**
 * @typedef {Object} IdOnlyClaims
 * @property {Array<string>} [amr] The authentication methods.
 * @property {string} [at_hash] The access token hash.
 * @property {string} [email] The email.
 * @property {string} [family_name] The last name (standard).
 * @property {string} [first_name] The first name (non-standard).
 * @property {string} [given_name] The first name (standard).
 * @property {boolean} [has_active_plans] The user has active plans.
 * @property {string} [idp] The identity provider.
 * @property {boolean} [is_trial] Trial user.
 * @property {string} [last_name] The last name (non-standard).
 * @property {string} [name] The name.
 * @property {string} [nickname] The nickname.
 * @property {string} [nonce] The nonce.
 * @property {string} [organization_name] The organization name.
 * @property {string} [picture] The picture.
 * @property {string} [preferred_username] The preferred name.
 * @property {string} [program_id] The program identifier.
 */

/**
 * @typedef {Object} AccessOnlyClaims
 * @property {string} [api_key] The API key (avoid this if possible).
 * @property {string} [azp] The authorized party.
 * @property {string} [cid] The client identifier.
 * @property {string} [pl_principal] The authenticated actor.
 * @property {string} [scope] The scopes.
 * @property {Array<string>} [scp] The scopes (non-standard, from Okta).
 * @property {string} [permissions] The permissions.
 * @property {string} [uid] The user identifier.
 */

/**
 * @typedef {Object} CommonClaims
 * @property {number} [auth_time] The time when authentication occurred.
 * @property {string} [organization_id] The organization identifier.
 * @property {number} [pl_user_id] The doc.
 * @property {number} [role_level] The role level.
 * @property {string|number} [user_id] The user id (avoid this - may be Planet id or may be Auth0 id).
 * @property {number} [ver] The version.
 */

/**
 * @typedef {Object} RegisteredClaims
 * @property {string|Array<string>} [aud] The audience.
 * @property {number} [exp] The expiry time.
 * @property {number} [iat] The issued at time.
 * @property {string} [iss] The issuer.
 * @property {string} [jti] The unique JWT identifier.
 * @property {number} [nbf] The not before time.
 * @property {string} [sub] The subject.
 */

/**
 * @typedef {Object<string, unknown>} UnknownClaims
 */

/**
 * @typedef {RegisteredClaims & AccessOnlyClaims & CommonClaims & UnknownClaims} AccessTokenClaims
 */

/**
 * @typedef {RegisteredClaims & IdOnlyClaims & CommonClaims & UnknownClaims} IdTokenClaims
 */

/**
 * @param {string} json The JSON input.
 * @return {Object<string, unknown>} An object.
 */
function parseObject(json) {
  const object = JSON.parse(json);
  if (!object || typeof object !== 'object' || Array.isArray(object)) {
    throw new Error('Expected an object');
  }

  return object;
}

/**
 * Parse id token claims.  Throws if claim types are unexpected or if the
 * token has expired.
 * @param {string} token The JWT.
 * @return {IdTokenClaims} The claims.
 */
export function parseIdTokenClaims(token) {
  const claims = parseRegisteredClaims(token);
  if ('email' in claims && typeof claims.email !== 'string') {
    throw new Error('Unexpected "email" claim');
  }

  return claims;
}

/**
 * Parse access token claims.  Throws if claim types are unexpected or if the
 * token has expired.
 * @param {string} token The JWT.
 * @return {AccessTokenClaims} The claims.
 */
function parseAccessTokenClaims(token) {
  const claims = parseRegisteredClaims(token);
  if ('api_key' in claims && typeof claims.api_key !== 'string') {
    throw new Error('Unexpected "api_key" claim');
  }

  return claims;
}

/**
 * Parse registered JWT claims.  Throws if claim types are unexpected or if the
 * token has expired.
 * @param {string} token The JWT.
 * @return {RegisteredClaims & UnknownClaims} The claims.
 */
function parseRegisteredClaims(token) {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT');
  }
  const payload = parts[1];
  const claims = parseObject(decoder.decode(decodeBase64Url(payload)));

  const now = Math.floor(Date.now() / 1000);

  if ('exp' in claims) {
    if (typeof claims.exp !== 'number') {
      throw new Error('Unexpected "exp" claim');
    }

    if (claims.exp <= now - clockTolerance) {
      throw new Error('Expired token');
    }
  }

  if ('iat' in claims && typeof claims.iat !== 'number') {
    throw new Error('Unexpected "iat" claim');
  }

  if ('iss' in claims && typeof claims.iss !== 'string') {
    throw new Error('Unexpected "iss" claim');
  }

  if ('nbf' in claims) {
    if (typeof claims.nbf !== 'number') {
      throw new Error('Unexpected "nbf" claim');
    }
    if (claims.nbf > now + clockTolerance) {
      throw new Error('Token cannot yet be used');
    }
  }

  if (
    'aud' in claims &&
    typeof claims.aud !== 'string' &&
    !Array.isArray(claims.aud)
  ) {
    throw new Error('Unexpected "aud" claim');
  }

  if ('jti' in claims && typeof claims.jti !== 'string') {
    throw new Error('Unexpected "jti" claim');
  }

  if ('sub' in claims && typeof claims.sub !== 'string') {
    throw new Error('Unexpected "sub" claim');
  }

  return claims;
}

/**
 * An error thrown when the response to the Authentication Request indicates an error.
 * See https://openid.net/specs/openid-connect-core-1_0.html#AuthError
 */
export class AuthenticationError extends Error {
  /**
   * @param {string} code The error code.
   * @param {string|null} [message] The error message.
   */
  constructor(code, message) {
    super(message || `Authentication error: ${code}`);
    this.code = code;
    this.name = 'AuthenticationError';
  }

  toString() {
    return `${super.toString()} (${this.code})`;
  }
}
